import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays, faChevronLeft, faChevronRight, faClock, faVideo,
  faUser, faEnvelope, faPhone, faCheck, faArrowRight, faLocationDot,
  faCircleCheck, faShieldHalved, faComments, faBuilding
} from "@fortawesome/free-solid-svg-icons";
import Logo from "../Images/logo 1.png";
import "../Styles/ScheduleAppointment.css";

const STEPS = [
  { id: 1, label: "Date & Time" },
  { id: 2, label: "Your Details" },
  { id: 3, label: "Confirmed" },
];

const SERVICES = [
  { value: "consultation", label: "General Consultation" },
  { value: "life-insurance", label: "Life Insurance" },
  { value: "retirement", label: "Retirement Planning" },
  { value: "wealth", label: "Wealth Building" },
  { value: "kai-zen", label: "Kai-Zen Plan" },
  { value: "business", label: "Business Protection" },
];

const MEETING_TYPES = [
  { value: "video", label: "Video Call", icon: faVideo, hint: "Google Meet link sent by email" },
  { value: "phone", label: "Phone Call", icon: faPhone, hint: "We call you at the scheduled time" },
  { value: "office", label: "In Person", icon: faBuilding, hint: "Brooklyn Center, MN office" },
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_NAMES = ["SU","MO","TU","WE","TH","FR","SA"];
const MORNING = ["09:00 AM","09:30 AM","10:00 AM","10:30 AM","11:00 AM","11:30 AM"];
const AFTERNOON = ["12:00 PM","12:30 PM","01:00 PM","01:30 PM","02:00 PM","02:30 PM"];
const EVENING = ["03:00 PM","03:30 PM","04:00 PM","04:30 PM","05:00 PM","05:30 PM"];

function ScheduleAppointment() {
  const [step, setStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [meetingType, setMeetingType] = useState("video");
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", service: "", notes: "" });

  const daysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const firstWeekday = (d) => new Date(d.getFullYear(), d.getMonth(), 1).getDay();

  const isPast = (day) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day) < today;
  };
  const isWeekend = (day) => {
    const wd = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).getDay();
    return wd === 0 || wd === 6;
  };
  const isToday = (day) => {
    const t = new Date();
    return day === t.getDate() && currentMonth.getMonth() === t.getMonth() && currentMonth.getFullYear() === t.getFullYear();
  };

  const pickDate = (day) => {
    if (isPast(day) || isWeekend(day)) return;
    setSelectedDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
    setSelectedTime(null);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetAll = () => {
    setStep(1); setSelectedDate(null); setSelectedTime(null); setMeetingType("video");
    setForm({ fullName: "", email: "", phone: "", service: "", notes: "" });
  };

  const longDate = selectedDate
    ? selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : null;

  const activeMeeting = MEETING_TYPES.find((m) => m.value === meetingType);
  const activeService = SERVICES.find((s) => s.value === form.service);

  const grid = [];
  for (let i = 0; i < firstWeekday(currentMonth); i++) grid.push(<span key={`pad-${i}`} className="cal-cell cal-pad" />);
  for (let day = 1; day <= daysInMonth(currentMonth); day++) {
    const disabled = isPast(day) || isWeekend(day);
    const selected = selectedDate && day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() && currentMonth.getFullYear() === selectedDate.getFullYear();
    grid.push(
      <button
        key={day} type="button" disabled={disabled} onClick={() => pickDate(day)}
        className={`cal-cell cal-day${disabled ? " is-off" : ""}${isToday(day) ? " is-today" : ""}${selected ? " is-selected" : ""}`}
        aria-label={`${MONTHS[currentMonth.getMonth()]} ${day}`} aria-pressed={selected || false}
      >
        {day}
        {!disabled && <i className="cal-dot" />}
      </button>
    );
  }

  const TimeGroup = ({ title, slots }) => (
    <div className="slot-group">
      <span className="slot-group-title">{title}</span>
      <div className="slot-row">
        {slots.map((t) => (
          <button
            key={t} type="button" onClick={() => setSelectedTime(t)}
            className={`slot${selectedTime === t ? " is-selected" : ""}`} aria-pressed={selectedTime === t}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="appointment-page">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="bk-hero">
        <div className="bk-hero-bg">
          <span className="bk-orb bk-orb-1" />
          <span className="bk-orb bk-orb-2" />
          <span className="bk-hero-grid" />
        </div>
        <div className="container bk-hero-inner">
          <span className="section-tag">Book a Consultation</span>
          <h1>Let's Talk About Your Financial Future</h1>
          <p>Pick a time that works for you. Free 45-minute session, zero obligation.</p>

          {/* Progress Stepper */}
          <div className="bk-stepper" role="list">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div
                  className={`bk-step${step === s.id ? " is-active" : ""}${step > s.id ? " is-done" : ""}`}
                  role="listitem" aria-current={step === s.id ? "step" : undefined}
                >
                  <span className="bk-step-dot">
                    {step > s.id ? <FontAwesomeIcon icon={faCheck} /> : s.id}
                  </span>
                  <span className="bk-step-label">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <span className={`bk-step-bar${step > s.id ? " is-done" : ""}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOOKING ===== */}
      <section className="bk-section">
        <div className="container">
          <div className={`bk-shell${step === 3 ? " is-single" : ""}`}>

            {/* ---- Live Summary Panel ---- */}
            {step !== 3 && (
              <aside className="bk-summary">
                <div className="bk-summary-head">
                  <img src={Logo} alt="Ebenezer Family Group" className="bk-summary-logo" />
                  <span className="bk-summary-badge">
                    <FontAwesomeIcon icon={faComments} /> Free Session
                  </span>
                  <h3>Financial Consultation</h3>
                  <p>A relaxed conversation about your goals. No sales pitch, no pressure.</p>
                </div>

                <ul className="bk-summary-list">
                  <li className="is-filled">
                    <FontAwesomeIcon icon={faClock} className="bk-sl-icon" />
                    <div><span className="bk-sl-key">Duration</span><span className="bk-sl-val">45 minutes</span></div>
                  </li>
                  <li className={selectedDate ? "is-filled" : ""}>
                    <FontAwesomeIcon icon={faCalendarDays} className="bk-sl-icon" />
                    <div>
                      <span className="bk-sl-key">Date</span>
                      <span className="bk-sl-val">{longDate || "Not selected yet"}</span>
                    </div>
                  </li>
                  <li className={selectedTime ? "is-filled" : ""}>
                    <FontAwesomeIcon icon={faClock} className="bk-sl-icon" />
                    <div>
                      <span className="bk-sl-key">Time</span>
                      <span className="bk-sl-val">{selectedTime ? `${selectedTime} (CST)` : "Not selected yet"}</span>
                    </div>
                  </li>
                  <li className="is-filled">
                    <FontAwesomeIcon icon={activeMeeting.icon} className="bk-sl-icon" />
                    <div><span className="bk-sl-key">Format</span><span className="bk-sl-val">{activeMeeting.label}</span></div>
                  </li>
                </ul>

                <div className="bk-summary-foot">
                  <div className="bk-trust"><FontAwesomeIcon icon={faShieldHalved} /><span>Your information stays private</span></div>
                  <div className="bk-trust"><FontAwesomeIcon icon={faCircleCheck} /><span>Reschedule anytime, no fees</span></div>
                </div>
              </aside>
            )}

            {/* ---- Main Panel ---- */}
            <div className="bk-main">

              {/* STEP 1 — Date + Time together */}
              {step === 1 && (
                <div className="bk-pane">
                  <div className="bk-pane-head">
                    <h2>Choose date &amp; time</h2>
                    <span className="bk-tz">Central Time (CST)</span>
                  </div>

                  <div className="bk-picker">
                    {/* Calendar */}
                    <div className="bk-cal">
                      <div className="cal-top">
                        <span className="cal-month">{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
                        <div className="cal-nav">
                          <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} aria-label="Previous month">
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <button type="button" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} aria-label="Next month">
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </div>
                      </div>
                      <div className="cal-weekdays">{DAY_NAMES.map((d) => <span key={d}>{d}</span>)}</div>
                      <div className="cal-grid">{grid}</div>
                      <div className="cal-legend">
                        <span><i className="lg-dot lg-open" /> Available</span>
                        <span><i className="lg-dot lg-off" /> Unavailable</span>
                      </div>
                    </div>

                    {/* Time slots */}
                    <div className="bk-slots">
                      {!selectedDate ? (
                        <div className="bk-slots-empty">
                          <FontAwesomeIcon icon={faCalendarDays} />
                          <p>Pick a date to see open times</p>
                        </div>
                      ) : (
                        <>
                          <span className="bk-slots-date">{selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</span>
                          <TimeGroup title="Morning" slots={MORNING} />
                          <TimeGroup title="Afternoon" slots={AFTERNOON} />
                          <TimeGroup title="Evening" slots={EVENING} />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Meeting format */}
                  <div className="bk-format">
                    <span className="bk-field-label">How would you like to meet?</span>
                    <div className="bk-format-row">
                      {MEETING_TYPES.map((m) => (
                        <button
                          key={m.value} type="button" onClick={() => setMeetingType(m.value)}
                          className={`bk-format-card${meetingType === m.value ? " is-selected" : ""}`} aria-pressed={meetingType === m.value}
                        >
                          <FontAwesomeIcon icon={m.icon} />
                          <strong>{m.label}</strong>
                          <small>{m.hint}</small>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button" className="bk-cta" disabled={!selectedDate || !selectedTime}
                    onClick={() => setStep(2)}
                  >
                    Continue to your details <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              )}

              {/* STEP 2 — Details */}
              {step === 2 && (
                <div className="bk-pane">
                  <button type="button" className="bk-back" onClick={() => setStep(1)}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Back to date &amp; time
                  </button>

                  <div className="bk-pane-head">
                    <h2>Tell us about you</h2>
                    <span className="bk-tz">Takes under a minute</span>
                  </div>

                  <form className="bk-form" onSubmit={submit}>
                    <div className="bk-field">
                      <label htmlFor="fullName"><FontAwesomeIcon icon={faUser} /> Full name *</label>
                      <input id="fullName" name="fullName" type="text" required value={form.fullName} onChange={handleChange} placeholder="Jane Doe" />
                    </div>

                    <div className="bk-field-row">
                      <div className="bk-field">
                        <label htmlFor="email"><FontAwesomeIcon icon={faEnvelope} /> Email *</label>
                        <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="jane@example.com" />
                      </div>
                      <div className="bk-field">
                        <label htmlFor="phone"><FontAwesomeIcon icon={faPhone} /> Phone *</label>
                        <input id="phone" name="phone" type="tel" required value={form.phone} onChange={handleChange} placeholder="+1 (763) 000-0000" />
                      </div>
                    </div>

                    <div className="bk-field">
                      <span className="bk-field-label">What would you like to discuss? *</span>
                      <div className="bk-chips">
                        {SERVICES.map((s) => (
                          <button
                            key={s.value} type="button" onClick={() => setForm({ ...form, service: s.value })}
                            className={`bk-chip${form.service === s.value ? " is-selected" : ""}`} aria-pressed={form.service === s.value}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="bk-field">
                      <label htmlFor="notes">Anything we should know? <span className="bk-optional">(optional)</span></label>
                      <textarea id="notes" name="notes" rows="3" value={form.notes} onChange={handleChange} placeholder="Share context so we can prepare for your session." />
                    </div>

                    <button type="submit" className="bk-cta" disabled={!form.service}>
                      Confirm appointment <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <p className="bk-fineprint">
                      By confirming, you agree to receive appointment reminders by email and SMS. No marketing spam.
                    </p>
                  </form>
                </div>
              )}

              {/* STEP 3 — Confirmation */}
              {step === 3 && (
                <div className="bk-done">
                  <div className="bk-done-check"><FontAwesomeIcon icon={faCheck} /></div>
                  <h2>You're all set, {form.fullName.split(" ")[0]}!</h2>
                  <p className="bk-done-sub">
                    A confirmation is on its way to <strong>{form.email}</strong>.
                    We'll send a reminder 24 hours before your session.
                  </p>

                  <div className="bk-ticket">
                    <div className="bk-ticket-row">
                      <FontAwesomeIcon icon={faCalendarDays} />
                      <div><span>Date</span><strong>{longDate}</strong></div>
                    </div>
                    <div className="bk-ticket-row">
                      <FontAwesomeIcon icon={faClock} />
                      <div><span>Time</span><strong>{selectedTime} CST · 45 min</strong></div>
                    </div>
                    <div className="bk-ticket-row">
                      <FontAwesomeIcon icon={activeMeeting.icon} />
                      <div><span>Format</span><strong>{activeMeeting.label}</strong></div>
                    </div>
                    <div className="bk-ticket-row">
                      <FontAwesomeIcon icon={faComments} />
                      <div><span>Topic</span><strong>{activeService ? activeService.label : "General Consultation"}</strong></div>
                    </div>
                    {meetingType === "office" && (
                      <div className="bk-ticket-row">
                        <FontAwesomeIcon icon={faLocationDot} />
                        <div><span>Location</span><strong>6160 Summit Dr N Suite #145, Brooklyn Center, MN</strong></div>
                      </div>
                    )}
                  </div>

                  <div className="bk-done-next">
                    <h4>What happens next</h4>
                    <ol>
                      <li>You'll get a confirmation email with all the details.</li>
                      <li>Our advisor reviews your notes and prepares for the session.</li>
                      <li>We meet, listen, and map out your options — no obligation.</li>
                    </ol>
                  </div>

                  <div className="bk-done-actions">
                    <Link to="/" className="btn-primary">Back to Home <FontAwesomeIcon icon={faArrowRight} /></Link>
                    <button type="button" className="btn-secondary" onClick={resetAll}>Book another session</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ScheduleAppointment;
